import { SttChunkStateStatus } from '@ftr/contracts/regional-api/stt'
import { generateUuid } from '@ftr/contracts/type/shared'
import { Inclusivity, JsJodaUtils, Optional } from '@ftr/foundation'
import { LocalDateTime } from '@js-joda/core'
import { SttRemark } from '../../../types'
import { SttRemarkWithMetadata } from './SttRemarkWithMetadata'

export type SttRemarkStatus = 'Nothing' | 'Requested' | 'Failed' | 'Content'

/**
 * The following states are unsupported
 *
 * The only way they can exist, is if 2 STT tasks for the same chunk were triggered at the same time.
 * In that case, we just take the first event and filter out the second.
 *
 * FailureToFailure
 * FailureToContent
 */
export enum UpdateType {
  // Means a new TRM was added
  NothingToGenerating,
  // Means STT generation succeeded
  GeneratingToContent,
  // Means there was no detectable audio in the segment
  GeneratingToNothing,
  // Means STT generation failed
  GeneratingToFailure,
  // Means STT was regenerated
  ContentToGenerating,
  // Means we have retried stt generation
  FailedToGenerating,
  // Should not be possible, something has gone wrong
  ContentToContent,
  // Should not be possible, essentially means the remarks array was empty
  Unknown,
  // An SttBegun is emitted for the entire recording when STT is requested.
  // Subsequent events eventually come in to update the original one.
  GeneratingToGenerating,
  // Live STT transitions from Nothing to Content
  NothingToContent,
  // Live STT transitions from Nothing to Failed
  NothingToFailed,
}

/**
 * Works with SttRemark/SttRemarkWithMetadata/SttRemarkWithPlayerInformation if you just need the start/end time
 */
export interface SttRemarkRange {
  startTime: LocalDateTime
  endTime: LocalDateTime
}

export class SttRemarkUtils {
  // eslint-disable-next-line complexity
  public static getUpdateType(current: SttRemarkStatus, updated: SttRemarkStatus): UpdateType {
    if (current === 'Nothing' && updated === 'Requested') {
      return UpdateType.NothingToGenerating
    } else if (current === 'Requested' && updated === 'Requested') {
      return UpdateType.GeneratingToGenerating
    } else if (current === 'Requested' && updated === 'Content') {
      return UpdateType.GeneratingToContent
    } else if (current === 'Requested' && updated === 'Nothing') {
      return UpdateType.GeneratingToNothing
    } else if (current === 'Requested' && updated === 'Failed') {
      return UpdateType.GeneratingToFailure
    } else if (current === 'Content' && updated === 'Requested') {
      return UpdateType.ContentToGenerating
    } else if (current === 'Failed' && updated === 'Requested') {
      return UpdateType.FailedToGenerating
    } else if (current === 'Content' && updated === 'Content') {
      return UpdateType.ContentToContent
    } else if (current === 'Nothing' && updated === 'Content') {
      // live streaming
      return UpdateType.NothingToContent
    } else if (current === 'Nothing' && updated === 'Failed') {
      // live streaming
      return UpdateType.NothingToFailed
    } else {
      return UpdateType.Unknown
    }
  }

  /**
   * The previous STT model had chunks with a status of begun, failed or finalized.
   * The new STT model does not have a status for finalized - so a finalized remark is one without a status.
   * @param remark
   */
  public static isFinalized(remark: SttRemark | SttRemarkWithMetadata | undefined): boolean {
    return remark?.status === undefined
  }

  /**
   * It's assumed the current remark will always have been transcribed and have a speaker id and start time
   * Show the remark's speaker name when:
   * - the current speaker id is different to the previous speaker id; or
   * - it's the first remark in the segment; or
   * - the previous remark has no speaker id; or
   * - the previous remark is not finalized; or
   * - it's the first remark with a start time within that minute;
   */
  public static isFirstRemarkOfSpeaker(
    remark: SttRemark | SttRemarkWithMetadata | undefined,
    prevRemark: SttRemark | SttRemarkWithMetadata | undefined,
  ): boolean {
    return !(this.isFinalized(prevRemark) && this.isSameUtterance(remark, prevRemark))
  }

  public static isSameUtterance(
    remark: SttRemark | SttRemarkWithMetadata | undefined,
    otherRemark: SttRemark | SttRemarkWithMetadata | undefined,
  ): boolean {
    return Optional.exists2(remark, otherRemark, (a, b) => this.isSameSpeaker(a, b) && this.isSameMinute(a, b))
  }

  public static isFirstRemarkOfMinute(
    remark: SttRemark | SttRemarkWithMetadata | undefined,
    prevRemark: SttRemark | SttRemarkWithMetadata | undefined,
  ): boolean {
    return (
      prevRemark?.startTime === undefined ||
      !this.isSameMinute(remark, prevRemark) ||
      !!prevRemark?.isSealedContentMarker
    )
  }

  public static isSameMinute(
    remark: SttRemark | SttRemarkWithMetadata | undefined,
    otherRemark: SttRemark | SttRemarkWithMetadata | undefined,
  ): boolean {
    return Optional.exists2(remark?.startTime, otherRemark?.startTime, (a, b) => a.minute() === b.minute())
  }

  public static isSameSpeaker(
    remark: SttRemark | SttRemarkWithMetadata,
    otherRemark: SttRemark | SttRemarkWithMetadata,
  ): boolean {
    return remark.speakerId === otherRemark.speakerId
  }

  static isDuring(
    remark: SttRemark | SttRemarkWithMetadata,
    playerTime: LocalDateTime | undefined,
    toleranceMs: number = 0,
    inclusive: Inclusivity = Inclusivity.Open,
  ): boolean {
    if (!playerTime) {
      return false
    }

    return JsJodaUtils.isDuring(remark.startTime, remark.endTime, playerTime, toleranceMs, inclusive)
  }

  /**
   * The intention is that this should be 1 TRM's worth of remarks.
   * This makes it easy to reason about. For example:
   * - Since TRM's get individually converted to text. It should not be possible to have both failed and content
   * remarks in the same list.
   *
   * All remarks are expected to be the same chunk state, but if they arent, we return Content
   */
  static getRemarkStatus(remarks: SttRemarkWithMetadata[] | SttRemark[]): SttRemarkStatus {
    if (!remarks.length) {
      return 'Nothing'
    } else if (remarks.length === 1) {
      return this.getRemarkStatusBasedOnSttChunkStateStatus(remarks[0].status)
    } else {
      return 'Content'
    }
  }

  static getRemarkStatusBasedOnSttChunkStateStatus(status: SttChunkStateStatus | undefined): SttRemarkStatus {
    switch (status) {
      case 'requested':
        return 'Requested'
      case 'failed':
        return 'Failed'
      case undefined:
        return 'Content'
      default:
        return 'Nothing'
    }
  }

  static startsBeforeAndEndsDuring(
    remark: SttRemarkWithMetadata,
    startTime: LocalDateTime,
    endTime: LocalDateTime,
    toleranceMs: number,
  ): boolean {
    return (
      JsJodaUtils.isBeforeWithTolerance(remark.startTime, startTime, toleranceMs) &&
      JsJodaUtils.isDuring(startTime, endTime, remark.endTime, toleranceMs)
    )
  }

  static generateId(): string {
    return 'remark-' + generateUuid()
  }

  /**
   * 2 SttRemarks are adjacent if they the start of one, is the end of the other
   */
  static isAdjacent(
    remarkA: SttRemarkWithMetadata | SttRemark,
    remarkB: SttRemarkWithMetadata | SttRemark,
    toleranceMs: number = 0,
  ): boolean {
    return (
      JsJodaUtils.isSameWithinTolerance(remarkA.endTime, remarkB.startTime, toleranceMs) ||
      JsJodaUtils.isSameWithinTolerance(remarkB.endTime, remarkA.startTime, toleranceMs)
    )
  }

  static isAdjacentWithSameStatus(
    remarkA: SttRemarkWithMetadata | SttRemark,
    remarkB: SttRemarkWithMetadata | SttRemark,
    toleranceMs: number = 0,
  ): boolean {
    return remarkA.status === remarkB.status && this.isAdjacent(remarkA, remarkB, toleranceMs)
  }

  static startsDuringAndEndsAfter(
    remark: SttRemarkWithMetadata | SttRemark,
    startTime: LocalDateTime,
    endTime: LocalDateTime,
    toleranceMs: number = 0,
  ): boolean {
    return (
      JsJodaUtils.isAfterWithTolerance(remark.endTime, endTime, toleranceMs) &&
      JsJodaUtils.isDuring(startTime, endTime, remark.startTime, toleranceMs)
    )
  }

  static startsBeforeAndEndsAfter(
    remark: SttRemarkWithMetadata | SttRemark,
    startTime: LocalDateTime,
    endTime: LocalDateTime,
  ): boolean {
    return remark.endTime.isAfter(endTime) && remark.startTime.isBefore(startTime)
  }

  /**
   * If this remark is fully contained within the start/end time (inclusive)
   */
  static isWithin(
    remark: SttRemarkWithMetadata | SttRemark,
    startTime: LocalDateTime,
    endTime: LocalDateTime,
    inclusive: Inclusivity = Inclusivity.Open,
  ): boolean {
    switch (inclusive) {
      case Inclusivity.Open:
      default:
        return (
          JsJodaUtils.isSameOrAfter(remark.startTime, startTime) && JsJodaUtils.isSameOrBefore(remark.endTime, endTime)
        )
      case Inclusivity.OpenClosed:
        return (
          JsJodaUtils.isSameOrAfter(remark.startTime, startTime) &&
          JsJodaUtils.isBeforeWithTolerance(remark.endTime, endTime)
        )
      case Inclusivity.ClosedOpen:
        return (
          JsJodaUtils.isAfterWithTolerance(remark.startTime, startTime) &&
          JsJodaUtils.isSameOrBefore(remark.endTime, endTime)
        )
      case Inclusivity.Closed:
        return (
          JsJodaUtils.isAfterWithTolerance(remark.startTime, startTime) &&
          JsJodaUtils.isBeforeWithTolerance(remark.endTime, endTime)
        )
    }
  }

  static isPartiallyWithin(
    remark: SttRemarkWithMetadata | SttRemark,
    startTime: LocalDateTime,
    endTime: LocalDateTime,
  ): boolean {
    return (
      SttRemarkUtils.startsBeforeAndEndsAfter(remark, startTime, endTime) ||
      SttRemarkUtils.isDuring(remark, startTime, 0, Inclusivity.OpenClosed) ||
      SttRemarkUtils.isDuring(remark, endTime, 0, Inclusivity.ClosedOpen)
    )
  }
}
